"use client";

import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Page-wide smooth scroll for the marketing landing, powered by Lenis.
 *
 * - Runs only in the browser (client component); mount-once via `useEffect`.
 * - Intercepts any in-page anchor click (`a[href^="#"]`) and hands it to
 *   Lenis so the scroll uses the same ease as wheel/touch — anchor jumps
 *   and wheel scroll feel like one motion.
 * - Respects `prefers-reduced-motion`: skips initialization entirely so
 *   native scroll + CSS smooth-scroll (which also respects that prefers
 *   rule) take over unchanged.
 * - Cleans up both the RAF loop and the click listener on unmount so
 *   navigating away (e.g. to `/app`) doesn't leave Lenis hijacking scroll
 *   on a page that manages its own overflow.
 *
 * Keep this mounted only on marketing pages — the authenticated app uses
 * its own scroll containers and should not have a document-level smoother.
 */
export function SmoothScroll() {
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const lenis = new Lenis({
      // Duration is the time Lenis takes to reach the target. 1.05s feels
      // premium without being sluggish — long scrolls still arrive fast
      // because Lenis also honours wheel velocity.
      duration: 1.05,
      // Exponential ease-out: fast start, gentle settle. Matches the
      // ease we use on `.reveal` transitions so the two visual motions
      // share the same character.
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      // Smooth wheel on desktop; leave touch native because phone OSes
      // already do an excellent job and their users expect that feel.
      smoothWheel: true,
      touchMultiplier: 1,
      // Disable for touch devices entirely — smoothing touch tends to
      // fight with momentum scrolling on iOS.
      syncTouch: false,
    });

    let rafId = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    // Read the CSS scroll-padding-top value so our anchor offset stays in
    // sync with whatever the stylesheet declares (currently 80px under
    // the sticky nav). If the CSS value ever changes, this follows.
    const scrollPadTop = (() => {
      const raw = getComputedStyle(document.documentElement).scrollPaddingTop;
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 80;
    })();

    const onClick = (e: MouseEvent) => {
      // Honour modifier clicks (new tab / window) and non-primary buttons.
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest<HTMLAnchorElement>('a[href^="#"]');
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href === "#") return;

      const el = document.querySelector(href);
      if (!el) return;

      e.preventDefault();
      lenis.scrollTo(el as HTMLElement, { offset: -scrollPadTop });
      // Keep the URL hash in sync without triggering another jump.
      if (history.replaceState) {
        history.replaceState(null, "", href);
      }
    };
    document.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      document.removeEventListener("click", onClick);
    };
  }, []);

  return null;
}
